import type { CatalogCategory, CatalogItem } from "@/lib/catalog";
import type { ClientSummary } from "@/lib/clients";
import { updateClient } from "@/lib/clients";
import type { TaxTreatment } from "@/lib/salesTax";
import type { LineItem } from "@/lib/types";

/**
 * Format a number as USD (Standard Quote rework, 2026-08-24): cents only
 * below $100, and then always two digits (`$0.20`, never `$0.2`); at or
 * above $100, whole dollars only, comma-grouped. Matches the design kit's
 * `money()` (`code/src/lib/demo-records.ts`) — previously this was a plain
 * `toFixed(2)`, which never comma-grouped large amounts and always forced
 * ".00" on whole-dollar amounts under $100.
 */
export const money = (n: number): string => {
  const v = Number.isFinite(n) ? n : 0;
  return (
    "$" +
    v.toLocaleString("en-US", {
      minimumFractionDigits: Math.abs(v) < 100 && !Number.isInteger(v) ? 2 : 0,
      maximumFractionDigits: Math.abs(v) < 100 ? 2 : 0,
    })
  );
};

/**
 * Always two decimals, comma-grouped — for a totals block where every row is
 * meant to be checked against the others (a GC verifying tax wants $124.70,
 * not $125). `money()`'s whole-dollar-above-$100 rounding is fine for lists
 * and cards shown on their own, but inside a totals summary it makes rows
 * that don't visibly reconcile. Matches the design kit's `moneyExact()`
 * (`code/src/lib/demo-records.ts`).
 */
export const moneyExact = (n: number): string =>
  "$" + (Number.isFinite(n) ? n : 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Always whole dollars, comma-grouped, cents always dropped regardless of
 * magnitude — for an aggregate/summary total (a client's total quoted, a
 * dashboard metric, a report row) where showing cents on a large rolled-up
 * number is noise, not precision. Deliberately a third function rather than
 * reusing `money()`: `money()` only drops cents above $100, so it would
 * still show "$45.30" for a small total — exactly what these call sites
 * don't want. Consolidated here (backlog UX-14) from an identical
 * `Intl.NumberFormat` literal copy-pasted as `fmtUSD` across ClientsPage,
 * ClientFormPage, ClientDetailPage, DashboardPage, DocumentsPage, and
 * ReportsPage.
 */
export const moneyWhole = (n: number): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(
    Number.isFinite(n) ? n : 0,
  );

/**
 * `category` is the source of truth once set (backlog G4 — Labor/Material/
 * Equipment). Lines saved before this shipped only ever had `type`
 * ("labor" | "material", no Equipment split) — falling back to it here is
 * what keeps those documents' totals computing exactly as they always did,
 * with no migration of stored JSON required.
 */
export function lineCategory(item: Pick<LineItem, "type" | "category">): CatalogCategory {
  return item.category ?? (item.type === "labor" ? "Labor" : "Material");
}

/**
 * A line's own *base* is qty × rate, full stop — markup was never baked in
 * here (backlog: Standard Quote review, 2026-08-24 — a prior version
 * applied markup at this exact point, so a line's total and the summary's
 * line-item subtotals never added up to the same number). Markup-inclusive
 * per-line *display* lives in the separate `lineDisplayTotal()`, added when
 * markup moved to the line level (backlog AUTH-8 follow-up, 2026-09-05) —
 * `lineDelta()` below now uses that one too (backlog PRINT-18), not this
 * one: a revision-delta comparison built on the pre-markup base could miss
 * a markup-only change entirely, or disagree with its own headline total.
 */
export function lineBase(item: LineItem): number {
  return (item.qty || 0) * (item.rate || 0);
}

/**
 * The markup % that actually applies to this line: its own override if set,
 * else the document's Markup % field — Labor is never marked up regardless
 * of either (backlog AUTH-8 follow-up, 2026-09-05, confirmed with Gabriel).
 * Floored at 0 (backlog PRINT-21) — every consumer of this function
 * (lineDisplayTotal, computeTotals, effectiveRate, lineDelta) goes through
 * here, so flooring at this single point guarantees a negative markup
 * (a stray "-", or a stored document predating input-layer validation)
 * can never produce a negative line total, regardless of whether the raw
 * per-line override or the document-level field itself was ever clamped.
 */
export function effectiveMarkupPct(item: LineItem, docMarkupPct: number): number {
  if (lineCategory(item) === "Labor") return 0;
  return Math.max(0, item.markupPct ?? docMarkupPct);
}

/**
 * What a line actually costs once its own effective markup is applied —
 * the number shown on both the builder and the printed/client-facing page,
 * so a line never shows two different prices depending on which screen
 * you're looking at (backlog AUTH-8 follow-up, 2026-09-05).
 */
export function lineDisplayTotal(item: LineItem, docMarkupPct: number): number {
  const base = lineBase(item);
  return base * (1 + effectiveMarkupPct(item, docMarkupPct) / 100);
}

/**
 * The per-unit rate that reconciles with `lineDisplayTotal()` — qty × this
 * always equals the line's displayed total. The client-facing page never
 * shows markup as its own figure, so it can't show the raw pre-markup rate
 * next to a post-markup total either: a customer multiplying qty × rate by
 * hand has to land on the number already printed as the total (2026-09-05 —
 * a $14 rate next to a $164.64 total at qty 10.5 doesn't multiply out).
 */
export function effectiveRate(item: LineItem, docMarkupPct: number): number {
  return (item.rate || 0) * (1 + effectiveMarkupPct(item, docMarkupPct) / 100);
}

/** Rounds to the nearest cent — the unit every dollar figure in `computeTotals` accumulates in, so its own sum reconciles exactly with what each rounded display formatter shows (backlog PRINT-19). */
function roundCents(n: number): number {
  return Math.round(n * 100) / 100;
}

export interface QuoteTotals {
  laborSubtotal: number;
  /** "Materials & equipment" — base cost, before markup. */
  materialsBase: number;
  materialsMarkup: number;
  taxAmount: number;
  grandTotal: number;
  /** Dollar amount the tax rate was applied to — shown in the Taxable base readout. */
  taxBase: number;
  taxedLines: number;
  exemptLines: number;
}

/**
 * Totals math (Standard Quote rework, DESIGN-SPEC.md §12.5–12.6): markup
 * applies to Material/Equipment only, per-line (each line's own
 * `effectiveMarkupPct` — `opts.markup` is now just the document-level
 * *default* a line without its own override falls back to, not a single
 * rate applied uniformly — backlog AUTH-8 follow-up, 2026-09-05).
 * Taxability is a property of the *line* alone now (`item.taxable`) — there
 * is no global category toggle to gate it (the old `taxLabor`/
 * `taxMaterials` checkboxes are gone; a line is seeded from the client's
 * job-state treatment at pick time, see `seedTaxable`, and can be flipped
 * per line). Lines saved before per-line `taxable` existed default to
 * taxable (today's behavior).
 *
 * Every line's contribution is rounded to cents before it's accumulated
 * (backlog PRINT-19) — a document with an ordinary rate-with-cents plus
 * the default markup could otherwise print a grand total a penny off from
 * hand-summing the printed line totals, since the unrounded fractional
 * cents from every line were quietly carried through the running sum and
 * only rounded once, at the very end. `materialsBase` itself is kept raw
 * (it's never printed alone, only combined with `materialsMarkup` — see
 * every call site), but `materialsMarkup` accumulates against the
 * *rounded* per-line total, not the raw one, so the combined figure still
 * reconciles exactly with the sum of what each line actually prints.
 */
export function computeTotals(items: LineItem[], opts: { markup: number; tax: number }): QuoteTotals {
  let laborSubtotal = 0;
  let materialsBase = 0;
  let materialsMarkup = 0;
  let taxBase = 0;
  let taxedLines = 0;
  let exemptLines = 0;

  for (const item of items) {
    const base = lineBase(item);
    const taxable = item.taxable !== false;
    let taxableAmount: number;
    if (lineCategory(item) === "Labor") {
      const roundedBase = roundCents(base);
      laborSubtotal += roundedBase;
      taxableAmount = roundedBase;
    } else {
      const roundedWithMarkup = roundCents(base * (1 + effectiveMarkupPct(item, opts.markup || 0) / 100));
      materialsBase += base;
      materialsMarkup += roundedWithMarkup - base;
      taxableAmount = roundedWithMarkup;
    }
    if (taxable) {
      taxBase += taxableAmount;
      taxedLines += 1;
    } else {
      exemptLines += 1;
    }
  }

  const taxAmount = roundCents(taxBase * ((opts.tax || 0) / 100));
  return {
    laborSubtotal,
    materialsBase,
    materialsMarkup,
    taxAmount,
    grandTotal: laborSubtotal + materialsBase + materialsMarkup + taxAmount,
    taxBase,
    taxedLines,
    exemptLines,
  };
}

export interface LineDeltaRow {
  /** `catalogItemId`, falling back to the line's own local `id` for a free-typed line — the only stable identity two independent line-item arrays share, so an edited catalog-backed line reads as "changed" rather than "removed"+"added". */
  key: string;
  desc: string;
  kind: "added" | "removed" | "changed";
  /** Markup-aware line total (`lineDisplayTotal`) — matches what the line itself displays, and what the headline `beforeTotal`/`afterTotal` are built from (backlog PRINT-18). */
  before: number;
  after: number;
  detail?: { qty?: [number, number]; rate?: [number, number]; markupPct?: [number, number] };
}

export interface LineDelta {
  rows: LineDeltaRow[];
  beforeTotal: number;
  afterTotal: number;
  delta: number;
  /** Percent change against `beforeTotal`, rounded to 1 decimal; 0 when there was nothing to compare against. */
  pct: number;
}

function lineDeltaKey(item: LineItem): string {
  return item.catalogItemId ?? `local:${item.id}`;
}

/**
 * "What changed, and by how much" between two line-item sets — the same
 * comparison used for a quote revision (against the quote it replaces) and a
 * quote→invoice conversion (against the quote it bills). The headline dollar
 * figures come from `computeTotals` (respects markup/tax), not a raw sum of
 * line totals, so they match what the sub and client actually see as "the
 * price."
 */
export function lineDelta(
  before: { items: LineItem[]; markup: string; tax: string },
  after: { items: LineItem[]; markup: string; tax: string },
): LineDelta {
  const rows: LineDeltaRow[] = [];
  const beforeByKey = new Map(before.items.map((item) => [lineDeltaKey(item), item]));
  const afterByKey = new Map(after.items.map((item) => [lineDeltaKey(item), item]));
  const beforeMarkup = Number(before.markup) || 0;
  const afterMarkup = Number(after.markup) || 0;

  for (const item of after.items) {
    const key = lineDeltaKey(item);
    const prev = beforeByKey.get(key);
    if (!prev) {
      rows.push({ key, desc: item.desc, kind: "added", before: 0, after: lineDisplayTotal(item, afterMarkup) });
      continue;
    }
    // Effective markup (not just the line's own `markupPct`) is part of the
    // change check — a line with no override that only moved because the
    // document's own Markup % changed is just as real a price change as an
    // edited qty/rate (backlog PRINT-18).
    const prevMarkupPct = effectiveMarkupPct(prev, beforeMarkup);
    const nextMarkupPct = effectiveMarkupPct(item, afterMarkup);
    if (prev.qty !== item.qty || prev.rate !== item.rate || prev.desc !== item.desc || prevMarkupPct !== nextMarkupPct) {
      rows.push({
        key,
        desc: item.desc,
        kind: "changed",
        before: lineDisplayTotal(prev, beforeMarkup),
        after: lineDisplayTotal(item, afterMarkup),
        detail: {
          ...(prev.qty !== item.qty ? { qty: [prev.qty, item.qty] as [number, number] } : {}),
          ...(prev.rate !== item.rate ? { rate: [prev.rate, item.rate] as [number, number] } : {}),
          ...(prevMarkupPct !== nextMarkupPct ? { markupPct: [prevMarkupPct, nextMarkupPct] as [number, number] } : {}),
        },
      });
    }
  }
  for (const item of before.items) {
    const key = lineDeltaKey(item);
    if (!afterByKey.has(key)) rows.push({ key, desc: item.desc, kind: "removed", before: lineDisplayTotal(item, beforeMarkup), after: 0 });
  }

  const beforeTotal = computeTotals(before.items, { markup: beforeMarkup, tax: Number(before.tax) || 0 }).grandTotal;
  const afterTotal = computeTotals(after.items, { markup: afterMarkup, tax: Number(after.tax) || 0 }).grandTotal;
  const delta = afterTotal - beforeTotal;

  return {
    rows,
    beforeTotal,
    afterTotal,
    delta,
    pct: beforeTotal === 0 ? 0 : Math.round((delta / beforeTotal) * 1000) / 10,
  };
}

/**
 * Seed a new line's taxable flag from the client's job-state tax treatment
 * (DESIGN-SPEC.md §12.5): a no-tax state exempts everything; a
 * labor-inclusive state (NM/HI/WV/SD) taxes everything, including labor;
 * otherwise labor defaults exempt and goods keep the catalog item's own
 * default. The sub can still flip any line by hand afterward.
 */
export function seedTaxable(category: CatalogCategory, itemTaxable: boolean, tx: TaxTreatment): boolean {
  if (tx.noTax) return false;
  if (tx.laborInclusive) return true;
  return category === "Labor" ? false : itemTaxable;
}

/**
 * Per-client materials markup override — a real column on the client
 * record (`clients.markup_override_pct`, migration 0010), mirroring
 * `@/lib/salesTax`'s tax override. A sub's hand-edited markup % for a
 * client is remembered and reused on that client's next quote.
 */
export function readMarkupOverride(client: Pick<ClientSummary, "markupOverridePct"> | null | undefined): number | null {
  return client?.markupOverridePct ?? null;
}

export async function saveMarkupOverride(clientId: string, pct: number): Promise<void> {
  await updateClient(clientId, { markupOverridePct: pct });
}

export async function clearMarkupOverride(clientId: string): Promise<void> {
  await updateClient(clientId, { markupOverridePct: null });
}

/**
 * A picked catalog item is *snapshotted* onto the line: name, unit, rate
 * and taxable are copied in now, so editing the catalog rate later never
 * rewrites a quote that already used it. `catalogItemId` is kept only as
 * provenance (usage counts, the ADJUSTED chip when the rate later diverges).
 */
export function lineFromCatalog(item: CatalogItem, id: number): LineItem {
  return {
    id,
    type: item.category === "Labor" ? "labor" : "material",
    category: item.category,
    desc: item.name,
    unit: item.unit,
    qty: 1,
    rate: item.rate,
    taxable: item.taxable,
    catalogItemId: item.id,
  };
}

export function blankLine(category: CatalogCategory, id: number, unit: string, taxable: boolean): LineItem {
  return {
    id,
    type: category === "Labor" ? "labor" : "material",
    category,
    desc: "",
    unit,
    qty: 1,
    rate: 0,
    taxable,
    catalogItemId: null,
  };
}

/** Progressive phone formatting, ported from ivFormatPhone. */
export function formatPhone(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 10);
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}`;
  if (d.length > 0) return `(${d}`;
  return "";
}

/** "Aug 22, 2026" — a new document's `date` field is stored in this display format, not ISO (see StandardQuote.date). */
export function todayDisplay(): string {
  return new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** Turn a doc id / name into a stable storage key. */
export function slugify(s: string): string {
  const x = (s || "").toString().trim().replace(/\s+/g, "-").replace(/["'\\/]+/g, "");
  return x.slice(0, 150) || "quote-" + Date.now();
}
